import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LogIn, Search, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, fmtQty, fmtDT } from "@/lib/api";

export default function PublicStock() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const load = () => api.get("/public/stock").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const items = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean).map((w) => new RegExp(`(^|[\\s·(/-])${w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`));
    return (d?.items || []).filter((i) => { const h = ` ${i.brand} ${i.cable_size} ${i.product_name} ${i.category}`.toLowerCase(); return words.every((r) => r.test(h)); });
  }, [d, q]);
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 bg-[#0B1530] text-white">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-3">
          <div className="h-8 w-8 rounded-md bg-red-600 grid place-items-center font-heading font-black">R</div>
          <div className="flex-1"><div className="font-heading font-bold leading-none">ROXY CABLES</div><div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">Live stock</div></div>
          <Button asChild size="sm" variant="secondary"><Link to="/login" data-testid="public-staff-login-btn"><LogIn className="h-4 w-4 mr-1" />Staff login</Link></Button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Available stock</h1>
            <p className="text-sm text-muted-foreground mt-1" data-testid="public-stock-asof">{d ? `Updated ${fmtDT(d.as_of)} · ${d.items.length} items` : "Loading…"}</p>
          </div>
          <Button variant="outline" size="icon" onClick={load} data-testid="public-stock-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
        <div className="relative"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search size, brand or cable type (e.g. 4 sq mm KEI)" className="pl-9 h-11" data-testid="public-stock-search" /></div>
        <div className="divide-y rounded-lg border bg-card" data-testid="public-stock-list">
          {items.map((i) => (
            <div key={i.product_id}>
              <button type="button" onClick={() => setOpen(open === i.product_id ? null : i.product_id)} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors" data-testid={`public-stock-item-${i.product_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{i.cable_size} · {i.brand}</div>
                  <div className="text-xs text-muted-foreground truncate">{i.product_name}</div>
                </div>
                <div className="text-right shrink-0"><div className="font-mono font-bold">{fmtQty(i.available, i.uom)}</div><div className="text-[11px] text-muted-foreground">{i.units.length} drum(s)/piece(s)</div></div>
              </button>
              {open === i.product_id && <div className="px-4 pb-3 flex flex-wrap gap-2">{i.units.map((u, k) => <span key={k} className="rounded border px-2 py-1 text-xs font-mono">{u.code} · {fmtQty(u.available, i.uom)}</span>)}</div>}
            </div>
          ))}
          {d && !items.length && <div className="p-6 text-sm text-muted-foreground text-center">No matching stock</div>}
        </div>
        <p className="text-[11px] text-muted-foreground">Stock changes through the day. Please call to confirm before collecting.</p>
      </main>
    </div>
  );
}
